import {
  type ActivityDisplayNode,
  groupActivityItemsForDisplay,
} from "./group-activity-items-for-display";
import type { ActivityRenderGroup, MessageViewPhase } from "./render-model";

export type ActivityStatusKind =
  | "preparing-response"
  | "preparing-next-step"
  | "reviewing-tool-result";

export type ActivityPresentationModel = {
  completed: ActivityDisplayNode[];
  current: ActivityDisplayNode | null;
  hasAnalysis: boolean;
  statusKind: ActivityStatusKind | null;
  toolCount: number;
};

const TERMINAL_TOOL_STATES = [
  "output-available",
  "output-error",
  "output-denied",
] as const;

function isToolDisplayNode(
  item: ActivityDisplayNode | undefined,
): item is Extract<
  ActivityDisplayNode,
  { kind: "tool" | "parallel-tool-batch" }
> {
  return item?.kind === "tool" || item?.kind === "parallel-tool-batch";
}

function isTerminalToolDisplayNode(
  item: Extract<ActivityDisplayNode, { kind: "tool" | "parallel-tool-batch" }>,
): boolean {
  if (item.kind === "parallel-tool-batch") {
    return item.state !== "running";
  }
  return TERMINAL_TOOL_STATES.some((state) => state === item.part.state);
}

/** Activity を、現在項目・完了履歴・アプリ所有の状態表示へ投影する。 */
export function deriveActivityPresentation({
  group,
  hasVisibleAnswer,
  phase,
}: {
  group: ActivityRenderGroup;
  hasVisibleAnswer: boolean;
  phase: MessageViewPhase;
}): ActivityPresentationModel {
  const items = groupActivityItemsForDisplay(group);
  const base = {
    hasAnalysis: items.some((item) => item.kind === "reasoning-group"),
    toolCount: group.toolCount,
  };

  if (phase === "awaiting-user") {
    const latestItem = items.at(-1);
    const current =
      isToolDisplayNode(latestItem) && !isTerminalToolDisplayNode(latestItem)
        ? latestItem
        : null;
    return {
      ...base,
      completed: current ? items.slice(0, -1) : items,
      current,
      statusKind: null,
    };
  }

  if (phase !== "live" || hasVisibleAnswer) {
    return {
      ...base,
      completed: items,
      current: null,
      statusKind: null,
    };
  }

  if (items.length === 0) {
    const latestStep = group.steps.at(-1);
    return {
      ...base,
      completed: [],
      current: null,
      statusKind:
        latestStep && latestStep.stepIndex > 0
          ? "preparing-next-step"
          : "preparing-response",
    };
  }

  const latestStep = group.steps.at(-1);
  if (latestStep?.items.length === 0) {
    return {
      ...base,
      completed: items,
      current: null,
      statusKind: "preparing-next-step",
    };
  }

  const latestItem = items.at(-1);
  if (!latestItem) {
    return {
      ...base,
      completed: [],
      current: null,
      statusKind: "preparing-response",
    };
  }

  if (isToolDisplayNode(latestItem) && isTerminalToolDisplayNode(latestItem)) {
    return {
      ...base,
      completed: items,
      current: null,
      statusKind: "reviewing-tool-result",
    };
  }

  if (latestItem.kind === "reasoning-group" && latestItem.state === "done") {
    return {
      ...base,
      completed: items,
      current: null,
      statusKind: "preparing-next-step",
    };
  }

  return {
    ...base,
    completed: items.slice(0, -1),
    current: latestItem,
    statusKind: null,
  };
}
